import axios, { AxiosResponse } from 'axios';
import { axiosRequestWithRetries } from 'utils/axios';
import { GeneratedAnswer } from './MetricsAITypes';
import { TimePeriods } from '../../Requirement';
import { nhost } from 'Providers/nhost/NhostProvider';

type PdfResponseType = {
  content: {
    page: number;
    text: string;
  }[];
};

const AI_BACKEND_URL = import.meta.env.REACT_APP_AI_BACKEND_URL;

export const extractTextFromPdf = async (
  fileUrl: string,
  fileName: string,
  requestSignal?: AbortSignal
): Promise<PdfResponseType> => {
  const accessToken = nhost.getUserSession()?.accessToken;
  return axiosRequestWithRetries(
    () =>
      axios.post(
        `${AI_BACKEND_URL}/docs/extract`,
        { fileUrl: fileUrl, name: fileName },
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          signal: requestSignal,
        }
      ),
    3,
    500
  );
};

export const extractNumericAnswer = async (
  metricTitleWithTags: string,
  unit: string,
  context: string,
  selectedQuarter: TimePeriods | undefined,
  requestSignal?: AbortSignal
): Promise<any> => {
  const accessToken = nhost.getUserSession()?.accessToken;
  return axios.post(
    `${AI_BACKEND_URL}/metrics/extractNumericAnswer`,
    {
      metricTitleWithTags,
      context,
      unit,
      selectedQuarter,
    },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      signal: requestSignal,
    }
  );
};

export const extractNarrativeAnswer = async (
  metricTitle: string,
  context: string,
  preferredLanguage: string,
  toneOfVoice?: string,
  requestSignal?: AbortSignal
): Promise<any> => {
  const accessToken = nhost.getUserSession()?.accessToken;
  return axios.post(
    `${AI_BACKEND_URL}/metrics/extractNarrativeAnswer`,
    {
      metricTitle,
      context,
      preferredLanguage,
      toneOfVoice,
    },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      signal: requestSignal,
    }
  );
};

export const extractManyNarrativeAnswers = async (
  metrics: { metricRef: string; metricTitle: string }[],
  context: string,
  preferredLanguage: string,
  toneOfVoice?: string,
  requestSignal?: AbortSignal
): Promise<GeneratedAnswer[]> => {
  const accessToken = nhost.getUserSession()?.accessToken;
  const res: AxiosResponse<{ answers: GeneratedAnswer[] }> = await axios.post(
    `${AI_BACKEND_URL}/metrics/extractManyNarrativeAnswers`,
    { metrics, context, preferredLanguage, toneOfVoice },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      signal: requestSignal,
    }
  );

  return res.data.answers;
};

export const summarizeNarrativeAnswers = async ({
  metricTitle,
  metricDescription,
  answers,
  isForBusinessUnits,
  preferredLanguage,
  toneOfVoice,
  requestSignal,
}: {
  metricTitle: string;
  metricDescription: string;
  answers: { unitName: string; answer: string }[];
  isForBusinessUnits: boolean;
  preferredLanguage: string;
  toneOfVoice?: string;
  requestSignal?: AbortSignal;
}): Promise<any> => {
  const accessToken = nhost.getUserSession()?.accessToken;
  return axios.post(
    `${AI_BACKEND_URL}/metrics/summarizeNarrativeAnswers`,
    {
      metricTitle,
      metricDescription,
      answers,
      isForBusinessUnits,
      preferredLanguage,
      toneOfVoice,
    },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      signal: requestSignal,
    }
  );
};
